import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Bell, BellOff, Archive, ArchiveRestore, Trash2, Check, CheckCheck, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper functions
const formatTime = (dateString) => {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: fr });
  } catch {
    return '';
  }
};

const formatFullDate = (dateString) => {
  try {
    return format(new Date(dateString), 'dd MMMM yyyy à HH:mm', { locale: fr });
  } catch {
    return '';
  }
};

const getNotificationIcon = (type) => {
  switch (type) {
    case 'cours_supprime':
      return <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center"><BellOff className="w-5 h-5 text-red-600" /></div>;
    case 'desinscription_par_instructeur':
      return <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center"><Bell className="w-5 h-5 text-orange-600" /></div>;
    case 'absence_instructeur':
    case 'absence_urgente':
      return <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>;
    default:
      return <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"><Bell className="w-5 h-5 text-blue-600" /></div>;
  }
};

// NotificationCard component
const NotificationCard = ({ 
  notif, 
  showActions = true, 
  isHighlighted, 
  highlightRef,
  onMarkAsRead,
  onArchive,
  onUnarchive,
  onDelete
}) => {
  return (
    <div 
      ref={isHighlighted ? highlightRef : null}
      className={`p-4 rounded-lg border transition-all ${
        isHighlighted 
          ? 'bg-yellow-50 border-yellow-400 border-2 shadow-md ring-2 ring-yellow-200' 
          : !notif.lu 
            ? 'bg-white border-l-4 border-l-[#E07A5F] shadow-sm' 
            : 'bg-white border-stone-200 shadow-sm'
      }`}
      data-testid={`notification-${notif.id}`}
    >
      <div className="flex items-start gap-3">
        {getNotificationIcon(notif.type || notif.type_notification)}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className={`font-medium ${!notif.lu ? 'text-stone-900' : 'text-stone-700'}`}>
                {notif.titre}
              </h3>
              <p className={`text-sm mt-1 whitespace-pre-line ${!notif.lu ? 'text-stone-600' : 'text-stone-500'}`}>
                {notif.contenu || notif.message}
              </p>
            </div>
            {!notif.lu && (
              <div className="w-2 h-2 rounded-full bg-[#E07A5F] flex-shrink-0 mt-2" />
            )}
          </div>
          
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2 text-xs text-stone-400">
              <span title={formatFullDate(notif.created_at)}>
                {formatTime(notif.created_at)}
              </span>
              {notif.archived && (
                <span className="px-2 py-0.5 bg-stone-200 rounded text-stone-500">
                  Archivée
                </span>
              )}
            </div>
            
            {showActions && (
              <div className="flex items-center gap-1">
                {!notif.lu && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onMarkAsRead(notif.id)}
                    title="Marquer comme lu"
                    className="h-8 px-2"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    <span className="text-xs">Lu</span>
                  </Button>
                )}
                
                {!notif.archived ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onArchive(notif.id)}
                    title="Archiver"
                    className="h-8 w-8"
                  >
                    <Archive className="w-4 h-4 text-stone-500" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onUnarchive(notif.id)}
                    title="Restaurer"
                    className="h-8 w-8"
                  >
                    <ArchiveRestore className="w-4 h-4 text-stone-500" />
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(notif.id)}
                  title="Supprimer définitivement"
                  className="h-8 w-8"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const Notifications = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('unread');
  const [stats, setStats] = useState({ unread: 0, read: 0, archived: 0 });
  const [highlightedId, setHighlightedId] = useState(null);
  const highlightRef = useRef(null);

  // Check for highlight parameter on mount
  useEffect(() => {
    const highlight = searchParams.get('highlight');
    if (highlight) {
      setHighlightedId(highlight);
      setActiveTab('unread'); // Start with unread tab
      // Clear the param from URL after reading
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Scroll to highlighted notification
  useEffect(() => {
    if (highlightedId && highlightRef.current && !loading) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Remove highlight after 3 seconds
      const timer = setTimeout(() => setHighlightedId(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightedId, loading, notifications]);

  const fetchNotifications = useCallback(async (filter) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/notifications?filter=${filter}&limit=50`);
      setNotifications(response.data.notifications || []);
      
      // Stats are now returned by the API
      setStats({
        unread: response.data.unread_count || 0,
        read: response.data.read_count || 0,
        archived: response.data.archived_count || 0
      });
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications(activeTab);
  }, [activeTab, fetchNotifications]);

  const handleMarkAsRead = async (notifId) => {
    try {
      // Marquer comme lue SANS archiver
      await axios.put(`${API_URL}/api/notifications/${notifId}/read?archive=false`);
      toast.success('Notification marquée comme lue');
      fetchNotifications(activeTab);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      // Marquer toutes comme lues SANS archiver
      await axios.put(`${API_URL}/api/notifications/read-all?archive=false`);
      toast.success('Toutes les notifications ont été marquées comme lues');
      fetchNotifications(activeTab);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleArchive = async (notifId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notifId}/archive`);
      toast.success('Notification archivée');
      fetchNotifications(activeTab);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUnarchive = async (notifId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notifId}/unarchive`);
      toast.success('Notification restaurée');
      fetchNotifications(activeTab);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (notifId) => {
    if (!window.confirm('Supprimer définitivement cette notification ?')) return;
    try {
      await axios.delete(`${API_URL}/api/notifications/${notifId}`);
      toast.success('Notification supprimée');
      fetchNotifications(activeTab);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif text-stone-800">Notifications</h1>
          <p className="text-sm text-stone-500 mt-1">
            Gérez vos notifications et consultez l&apos;historique
          </p>
        </div>
        
        {stats.unread > 0 && (
          <Button
            onClick={handleMarkAllAsRead}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="mark-all-read-btn"
          >
            <CheckCheck className="w-4 h-4" />
            Tout marquer comme lu ({stats.unread})
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="unread" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Non lues
            {stats.unread > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs bg-[#E07A5F] text-white rounded-full">
                {stats.unread}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="read" className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            Lues
          </TabsTrigger>
          <TabsTrigger value="archived" className="flex items-center gap-2">
            <Archive className="w-4 h-4" />
            Archivées
          </TabsTrigger>
        </TabsList>

        <TabsContent value="unread">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#E07A5F]" />
                Notifications non lues
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="spinner mx-auto" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-16 h-16 mx-auto text-stone-200 mb-4" />
                  <p className="text-stone-500">Aucune notification non lue</p>
                  <p className="text-sm text-stone-400 mt-1">Vous êtes à jour !</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <NotificationCard 
                      key={notif.id} 
                      notif={notif}
                      isHighlighted={notif.id === highlightedId}
                      highlightRef={highlightRef}
                      onMarkAsRead={handleMarkAsRead}
                      onArchive={handleArchive}
                      onUnarchive={handleUnarchive}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="read">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                Notifications lues (non archivées)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="spinner mx-auto" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Check className="w-16 h-16 mx-auto text-stone-200 mb-4" />
                  <p className="text-stone-500">Aucune notification lue non archivée</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <NotificationCard 
                      key={notif.id} 
                      notif={notif}
                      isHighlighted={notif.id === highlightedId}
                      highlightRef={highlightRef}
                      onMarkAsRead={handleMarkAsRead}
                      onArchive={handleArchive}
                      onUnarchive={handleUnarchive}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="archived">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Archive className="w-5 h-5 text-stone-500" />
                Notifications archivées (traçabilité)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="spinner mx-auto" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Archive className="w-16 h-16 mx-auto text-stone-200 mb-4" />
                  <p className="text-stone-500">Aucune notification archivée</p>
                  <p className="text-sm text-stone-400 mt-1">Les notifications archivées apparaîtront ici pour traçabilité</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <NotificationCard 
                      key={notif.id} 
                      notif={notif}
                      isHighlighted={notif.id === highlightedId}
                      highlightRef={highlightRef}
                      onMarkAsRead={handleMarkAsRead}
                      onArchive={handleArchive}
                      onUnarchive={handleUnarchive}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Notifications;
